import { useState, useEffect, useCallback } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useStore, usePrimaryAddress } from '../lib/store';
import { messages as msgApi, labels as labelsApi } from '../lib/api';
import { stripTrackers, decryptBlobWithNsec, decryptBlobWithExtension } from '../lib/crypto';
import type { MessageDetailResponse, Message } from '../lib/types';

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + ' ' + units[i];
}

interface ReaderState {
  loading: boolean;
  data: MessageDetailResponse | null;
  error: boolean;
  decryptedText: string | null;
  decrypting: boolean;
}

export function MessageReader() {
  const store = useStore();
  const { currentMessageId, session, setComposeOpen, showToast } = store;
  const inboxMessages = store.messages;
  const currentView = store.currentView;
  const primaryAddress = usePrimaryAddress();
  const [state, setState] = useState<ReaderState>({
    loading: false,
    data: null,
    error: false,
    decryptedText: null,
    decrypting: false,
  });

  const loadMessage = useCallback(
    async (id: string) => {
      setState({
        loading: true,
        data: null,
        error: false,
        decryptedText: null,
        decrypting: false,
      });
      try {
        const res = await msgApi.get(id);
        setState({
          loading: false,
          data: res,
          error: false,
          decryptedText: null,
          decrypting: false,
        });

        if (res.encrypted) {
          setState((s) => ({ ...s, decrypting: true }));
          try {
            let plaintext: Uint8Array;
            if (session?.secretKeyHex) {
              const rawBytes = await msgApi.getRaw(id);
              plaintext = await decryptBlobWithNsec(rawBytes, session.secretKeyHex);
            } else if (window.nostr?.nip44?.decrypt) {
              const rawBytes = await msgApi.getRaw(id);
              plaintext = await decryptBlobWithExtension(rawBytes);
            } else {
              setState((s) => ({
                ...s,
                decrypting: false,
                decryptedText:
                  'Sign in with nsec or a NIP-07 extension to decrypt this message.',
              }));
              return;
            }
            const text = new TextDecoder().decode(plaintext);
            setState((s) => ({ ...s, decrypting: false, decryptedText: text }));
          } catch (e) {
            console.error('Decryption error:', e);
            setState((s) => ({
              ...s,
              decrypting: false,
              decryptedText: 'Decryption failed. This message was encrypted to a different key.',
            }));
          }
        }
      } catch {
        setState({
          loading: false,
          data: null,
          error: true,
          decryptedText: null,
          decrypting: false,
        });
      }
    },
    [session],
  );

  useEffect(() => {
    if (currentMessageId) {
      void loadMessage(currentMessageId);
    } else {
      setState({
        loading: false,
        data: null,
        error: false,
        decryptedText: null,
        decrypting: false,
      });
    }
  }, [currentMessageId, loadMessage]);

  const handleDownloadAttachment = useCallback(
    async (msgId: string, attId: string) => {
      try {
        const blobData = await msgApi.getAttachment(msgId, attId);
        let plaintext: Uint8Array;
        if (session?.secretKeyHex) {
          plaintext = await decryptBlobWithNsec(blobData, session.secretKeyHex);
        } else if (window.nostr?.nip44?.decrypt) {
          plaintext = await decryptBlobWithExtension(blobData);
        } else {
          throw new Error('Sign in with nsec or extension to download attachments');
        }
        const dlBlob = new Blob([plaintext as unknown as BlobPart]);
        const url = URL.createObjectURL(dlBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'attachment-' + attId;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Attachment download error:', e);
        showToast('Download failed. Please try again.', 'error');
      }
    },
    [session, showToast],
  );

  const handleReply = useCallback(
    (fromAddr: string, subject: string) => {
      const subj = subject || '';
      const replySubject = subj.toLowerCase().startsWith('re:') ? subj : 'Re: ' + subj;
      sessionStorage.setItem('nomail_reply_to', fromAddr);
      sessionStorage.setItem('nomail_reply_subject', replySubject);
      setComposeOpen(true);
    },
    [setComposeOpen],
  );

  const closeMessage = useCallback(() => {
    const hash = location.hash.slice(1);
    if (hash.includes('/')) {
      const view = hash.split('/')[0];
      history.pushState(null, '', '#' + view);
    }
    store.setCurrentMessageId(null);
  }, [store]);

  const handleCopyAddress = useCallback(() => {
    if (!primaryAddress) {
      showToast('No address available', 'error');
      return;
    }
    const full = primaryAddress.localPart + '@' + primaryAddress.domain;
    navigator.clipboard.writeText(full).then(() => {
      showToast('Address copied: ' + full, 'success');
    });
  }, [primaryAddress, showToast]);

  const navigateToMessage = useCallback(
    (id: string) => {
      store.setCurrentMessageId(id);
      const newHash = '#' + currentView + '/' + id;
      if (location.hash !== newHash) history.pushState(null, '', newHash);
    },
    [store, currentView],
  );

  if (!currentMessageId) {
    return (
      <div class="reader" id="reader" role="region" aria-label="Message reader" data-testid="reader-pane">
        <div class="reader-empty" data-testid="reader-empty">
          <p>No message selected</p>
          <p style="font-size:12px">
            Select a message from the list, or copy your address to receive mail.
          </p>
          <div class="reader-empty-actions">
            <button class="btn btn-ghost btn-sm" onClick={handleCopyAddress}>
              Copy Address
            </button>
            <button class="btn btn-sm" onClick={() => setComposeOpen(true)}>
              Compose
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { loading, data, error, decryptedText, decrypting } = state;

  const threadSiblings: Message[] = data?.message?.threadId
    ? inboxMessages
        .filter((m) => m.threadId === data.message.threadId)
        .sort((a, b) => a.receivedAt - b.receivedAt)
    : [];

  return (
    <div
      class="reader mobile-active"
      id="reader"
      role="region"
      aria-label="Message reader"
      data-testid="reader-pane"
    >
      <div
        class="reader-back-btn"
        role="button"
        tabindex={0}
        onClick={closeMessage}
        onKeyDown={(e) => {
          if (e.key === 'Enter') closeMessage();
        }}
      >
        &lsaquo; Messages
      </div>
      <div id="reader-body">
        {loading && (
          <div class="reader-skeleton">
            <div class="skel-line title" />
            <div class="skel-line meta" />
            <div class="skel-line body" />
            <div class="skel-line body" />
            <div class="skel-line body" />
            <div class="skel-line body-short" />
          </div>
        )}

        {error && (
          <div class="error-retry">
            <p>Failed to load message</p>
            <button
              class="btn btn-ghost btn-sm"
              onClick={() => currentMessageId && loadMessage(currentMessageId)}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <div class="reader-content">
            <ReaderContent
              data={data}
              decryptedText={decryptedText}
              decrypting={decrypting}
              messageId={currentMessageId}
              threadSiblings={threadSiblings}
              onDownloadAttachment={handleDownloadAttachment}
              onReply={handleReply}
              onNavigateThread={navigateToMessage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface ReaderContentProps {
  data: MessageDetailResponse;
  decryptedText: string | null;
  decrypting: boolean;
  messageId: string;
  threadSiblings: Message[];
  onDownloadAttachment: (msgId: string, attId: string) => void;
  onReply: (fromAddr: string, subject: string) => void;
  onNavigateThread: (id: string) => void;
}

function ReaderContent({
  data,
  decryptedText,
  decrypting,
  messageId,
  threadSiblings,
  onDownloadAttachment,
  onReply,
  onNavigateThread,
}: ReaderContentProps) {
  const m = data.message;

  const cc = (data.recipients || [])
    .filter((r) => r.type === 'cc')
    .map((r) => r.address)
    .join(', ');

  let bodyContent: ComponentChildren;
  if (m.ingestStatus === 'failed' || m.ingestStatus === 'rejected') {
    const failedMsg =
      m.ingestStatus === 'rejected'
        ? 'This message was blocked.'
        : "This message couldn't be fully displayed.";
    bodyContent = (
      <div class="reader-parse-failed">
        <div class="pf-title">Message unavailable</div>
        <div class="pf-desc">
          {failedMsg} The email may be corrupted or in an unsupported format.
        </div>
      </div>
    );
  } else if (data.encrypted) {
    bodyContent = (
      <div
        class="reader-body"
        style={decrypting ? { color: 'var(--muted)' } : { color: 'var(--text)' }}
        data-testid="message-body"
      >
        {decrypting ? 'Decrypting...' : decryptedText || ''}
      </div>
    );
  } else if (m.htmlPreview) {
    const { cleaned, blocked } = stripTrackers(m.htmlPreview);
    const wrapped =
      '<base target="_blank"><body style="margin:12px;font-family:sans-serif;font-size:14px;line-height:1.6;color:#333;word-wrap:break-word;">' +
      cleaned +
      '</body>';
    bodyContent = (
      <>
        <iframe
          class="email-html-frame"
          sandbox=""
          srcDoc={wrapped}
          onLoad={(e) => {
            try {
              const frame = e.currentTarget as HTMLIFrameElement;
              const h = frame.contentWindow?.document.body.scrollHeight;
              if (h) frame.style.height = Math.min(h + 24, 600) + 'px';
            } catch {
              // cross-origin
            }
          }}
        />
        {blocked > 0 && (
          <div class="reader-tracker-note">
            <span class="blocked">
              {blocked} tracker{blocked > 1 ? 's' : ''}
            </span>{' '}
            blocked
          </div>
        )}
      </>
    );
  } else if (m.textPreview) {
    bodyContent = <div class="reader-body" data-testid="message-body">{m.textPreview}</div>;
  } else {
    bodyContent = (
      <div class="reader-body" style={{ color: 'var(--muted)' }} data-testid="message-body">
        No preview available
      </div>
    );
  }

  return (
    <>
      <ThreadSection
        siblings={threadSiblings}
        currentMessageId={messageId}
        onNavigate={onNavigateThread}
      />
      <div class="reader-subject" data-testid="message-subject">{m.subject || '(no subject)'}</div>
      <div class="reader-header-summary" data-testid="message-from">
        <strong>{m.fromName || m.fromAddr}</strong>
        <span class="reader-header-date">{formatTime(m.receivedAt)}</span>
      </div>
      <details class="reader-header-details">
        <summary class="reader-header-toggle">Details</summary>
        <div class="reader-header-expanded">
          <div class="reader-meta-line"><span class="reader-meta-label">From</span> {m.fromName || ''} {'<'}{m.fromAddr}{'>'}</div>
          <div class="reader-meta-line"><span class="reader-meta-label">To</span> {m.toAddr}</div>
          {cc && <div class="reader-meta-line"><span class="reader-meta-label">Cc</span> {cc}</div>}
          <div class="reader-meta-line"><span class="reader-meta-label">Date</span> {new Date(m.receivedAt).toUTCString()}</div>
        </div>
      </details>

      <LabelManager messageId={messageId} />

      {bodyContent}

      {data.attachments && data.attachments.length > 0 && (
        <div class="attachment-list">
          <div class="att-header">Attachments</div>
          {data.attachments.map((a) => (
            <div key={a.id} class="attachment-card">
              <span class="att-icon">&#128206;</span>
              <div class="att-info">
                <div class="att-name">{a.filename || 'attachment'}</div>
                {a.size != null && a.size > 0 && (
                  <div class="att-size">{formatFileSize(a.size)}</div>
                )}
              </div>
              <button
                class="att-download"
                onClick={() => onDownloadAttachment(messageId, a.id)}
                data-testid="download-attachment-btn"
              >
                Download
              </button>
            </div>
          ))}
        </div>
      )}

      <div class="reply-bar">
        <button
          class="btn btn-ghost btn-sm"
          onClick={() => onReply(m.fromAddr || '', m.subject || '')}
          data-testid="reply-btn"
        >
          Reply
        </button>
        <ThreadNav siblings={threadSiblings} currentMessageId={messageId} onNavigate={onNavigateThread} />
      </div>

      <details style="margin-top:20px">
        <summary style="cursor:pointer;color:var(--muted);font-size:12px">
          Show technical details
        </summary>
        <div style="margin-top:8px;font-size:12px;color:var(--muted)">
          <div>Status: {m.ingestStatus}</div>
          <div>Size: {m.rawSize} bytes</div>
          <div>ID: {m.id}</div>
        </div>
      </details>
    </>
  );
}

function LabelManager({ messageId }: { messageId: string }) {
  const { labels, setLabels, messages, setMessages, showToast } = useStore();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  const current = messages.find((m) => m.id === messageId);
  const applied = new Set(current?.labelIds || []);

  const toggle = useCallback(async (labelId: number, isApplied: boolean) => {
    const prev = messages;
    const optimistic = prev.map((m) =>
      m.id === messageId
        ? {
            ...m,
            labelIds: isApplied
              ? (m.labelIds || []).filter((id) => id !== labelId)
              : [...(m.labelIds || []), labelId],
          }
        : m,
    );
    setMessages(optimistic);
    try {
      if (isApplied) {
        await msgApi.removeLabel(messageId, labelId);
      } else {
        await msgApi.addLabel(messageId, labelId);
      }
    } catch {
      setMessages(prev);
      showToast('Failed to update label', 'error');
    }
  }, [messageId, messages, setMessages, showToast]);

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const created = await labelsApi.create(name);
      setLabels([...labels, created]);
      setNewName('');
      setShowCreate(false);
    } catch (e) {
      showToast((e as Error).message || 'Failed to create label', 'error');
    }
  }, [newName, labels, setLabels, showToast]);

  return (
    <div class="reader-labels" data-testid="reader-labels">
      <div class="reader-labels-header">
        <span class="reader-labels-title">Labels</span>
        <button
          type="button"
          class="btn btn-ghost btn-sm"
          onClick={() => setShowCreate((v) => !v)}
          data-testid="toggle-create-label"
        >
          {showCreate ? 'Cancel' : '+ New'}
        </button>
      </div>
      {showCreate && (
        <div class="label-create-row">
          <input
            type="text"
            class="label-create-input"
            placeholder="Label name..."
            maxLength={30}
            value={newName}
            onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
            data-testid="label-name-input"
          />
          <button
            type="button"
            class="btn btn-sm"
            onClick={() => void handleCreate()}
            disabled={!newName.trim()}
            data-testid="create-label-btn"
          >
            Add
          </button>
        </div>
      )}
      <div class="label-chips">
        {labels.length === 0 && !showCreate && (
          <span class="label-empty-hint">No labels yet</span>
        )}
        {labels.map((l) => {
          const isOn = applied.has(l.id);
          return (
            <button
              key={l.id}
              type="button"
              class={'label-chip' + (isOn ? ' applied' : '')}
              style={isOn && l.color ? { backgroundColor: l.color, borderColor: l.color } : undefined}
              onClick={() => void toggle(l.id, isOn)}
              data-testid="label-toggle"
            >
              {isOn ? '\u2713 ' : ''}
              {l.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ThreadSectionProps {
  siblings: Message[];
  currentMessageId: string;
  onNavigate: (id: string) => void;
}

function ThreadSection({ siblings, currentMessageId, onNavigate }: ThreadSectionProps) {
  if (siblings.length <= 1) return null;
  const currentIdx = siblings.findIndex((m) => m.id === currentMessageId);
  const position = currentIdx === -1 ? siblings.length : currentIdx + 1;

  return (
    <details class="thread-section" open data-testid="thread-section">
      <summary class="thread-section-summary">
        <span class="thread-section-icon" aria-hidden="true">{'\u21A9'}</span>
        Thread &middot; {siblings.length} messages
        <span class="thread-section-position">{position} of {siblings.length}</span>
      </summary>
      <ol class="thread-section-list">
        {siblings.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              class={'thread-section-item' + (m.id === currentMessageId ? ' current' : '')}
              onClick={() => m.id !== currentMessageId && onNavigate(m.id)}
              data-testid="thread-nav-item"
            >
              <span class="thread-section-from">{m.fromName || m.fromAddr || 'Unknown'}</span>
              <span class="thread-section-date">{formatTime(m.receivedAt)}</span>
            </button>
          </li>
        ))}
      </ol>
    </details>
  );
}

function ThreadNav({ siblings, currentMessageId, onNavigate }: ThreadSectionProps) {
  if (siblings.length <= 1) return null;
  const idx = siblings.findIndex((m) => m.id === currentMessageId);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;
  if (!prev && !next) return null;

  return (
    <span class="thread-nav">
      <button
        type="button"
        class="btn btn-ghost btn-sm"
        disabled={!prev}
        onClick={() => prev && onNavigate(prev.id)}
        title="Previous in thread"
        data-testid="thread-prev"
      >
        {'\u2039 Prev'}
      </button>
      <button
        type="button"
        class="btn btn-ghost btn-sm"
        disabled={!next}
        onClick={() => next && onNavigate(next.id)}
        title="Next in thread"
        data-testid="thread-next"
      >
        {'Next \u203A'}
      </button>
    </span>
  );
}
